// 3D Contour (Isosurface) generation using Marching Cubes.
// Exports:
// - createContour(ctx): builds isosurface mesh for given isoValue and adds to scene
// - destroyContour(ctx): removes mesh from scene and disposes resources
// - updateContour(ctx): regenerates geometry on an existing mesh for a new isoValue
//
// Note: ui:task:show/hide dispatches below are the allowed scene → store →
// ui direction for long-running progress signals. Scene never imports a UI
// module; UI drivers (panel or headless) subscribe to the intents.
//
// params contract — keys read from the reactive store:
//   contourValue, contourDomain
//   contourOpacity, contourMetalness, contourRoughness, contourEnvIntensity
//   contourDoubleSide, contourDepthWrite, contourRenderOrder
//
// Spatial clipping is driven solely by contourDomain: a Box/Sphere domain
// clips the isosurface to its bounds; 'full' means no clip.

import * as THREE from 'three';
import { store } from '../store/instance';
import { mapPhysicalToThreeWithRotation } from '@adapters/coordinates';
import { strengthToColor } from '../physics/colormap';
import { generateContourAsync } from '../workers/fieldlineClient';
import { INTENTS } from '../store/intents';
import { debugWarn } from '@adapters/debug';
import { resolveDomainBounds, isOutsideDomain } from '../store/selections';
import type { ViewerContext } from '../app/context';
import type { ParamsState } from '../store/schema';

// Marching tetrahedra decomposition (6 tets per cube)
const tetrahedra: ReadonlyArray<readonly [number, number, number, number]> = [
    [0,5,1,6],
    [0,1,2,6],
    [0,2,3,6],
    [0,3,7,6],
    [0,7,4,6],
    [0,4,5,6],
];

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

interface CreateContourCtx {
    THREE: typeof import('three');
    scene: THREE.Scene;
    FIELD_NX: number; FIELD_NY: number; FIELD_NZ: number;
    GRID_X_MIN: number; GRID_X_MAX: number;
    GRID_Y_MIN: number; GRID_Y_MAX: number;
    GRID_Z_MIN: number; GRID_Z_MAX: number;
    getScalarArray: () => Float32Array | null;
    physicalToThreeJS: (x: number, y: number, z: number) => THREE.Vector3;
    isoValue: number;
    materialColor?: number;
    materialOpacity?: number;
}

interface ContourResult {
    contourMesh: THREE.Mesh | null;
    contourMaterial: THREE.Material | null;
}

export function createContour(ctx: CreateContourCtx): ContourResult {
    const {
        THREE,
        scene,
        FIELD_NX, FIELD_NY, FIELD_NZ,
        GRID_X_MIN, GRID_X_MAX, GRID_Y_MIN, GRID_Y_MAX, GRID_Z_MIN, GRID_Z_MAX,
        getScalarArray,
        physicalToThreeJS,
        isoValue,
        materialColor,
        materialOpacity,
    } = ctx;

    const nx = FIELD_NX, ny = FIELD_NY, nz = FIELD_NZ;
    const xRange = GRID_X_MAX - GRID_X_MIN;
    const yRange = GRID_Y_MAX - GRID_Y_MIN;
    const zRange = GRID_Z_MAX - GRID_Z_MIN;
    const data = getScalarArray();
    if (!data || data.length !== nx * ny * nz) {
        console.warn('Contour: scalar field not available');
        return { contourMesh: null, contourMaterial: null };
    }

    // Compute data range to guard isoValue and avoid edge cases
    let fieldMin = Infinity, fieldMax = -Infinity;
    for (let it = 0; it < data.length; it++) { const v = data[it] ?? 0; if (v < fieldMin) fieldMin = v; if (v > fieldMax) fieldMax = v; }
    const range = fieldMax - fieldMin;
    if (!(range > 0)) {
        console.warn('Contour: degenerate scalar field');
        return { contourMesh: null, contourMaterial: null };
    }
    const eps = Math.max(1e-12, range * 1e-6);
    const iso = Math.min(fieldMax - eps, Math.max(fieldMin + eps, isoValue));

    function idx(i: number, j: number, k: number): number { return i * (ny * nz) + j * nz + k; }
    function val(i: number, j: number, k: number): number { return data![idx(i, j, k)] ?? 0; }

    const positions: number[] = [];
    const normals: number[] = [];

    for (let i = 0; i < nx - 1; i++) {
        for (let j = 0; j < ny - 1; j++) {
            for (let k = 0; k < nz - 1; k++) {
                const v: number[] = [
                    val(i, j, k),
                    val(i + 1, j, k),
                    val(i + 1, j + 1, k),
                    val(i, j + 1, k),
                    val(i, j, k + 1),
                    val(i + 1, j, k + 1),
                    val(i + 1, j + 1, k + 1),
                    val(i, j + 1, k + 1),
                ];

                // Corner positions in three.js space
                const p = new Array<THREE.Vector3>(8);
                function phys(i_: number, j_: number, k_: number): THREE.Vector3 {
                    const px = GRID_X_MIN + (i_ / (nx - 1)) * xRange;
                    const py = GRID_Y_MIN + (j_ / (ny - 1)) * yRange;
                    const pz = GRID_Z_MIN + (k_ / (nz - 1)) * zRange;
                    return physicalToThreeJS(px, py, pz);
                }
                p[0] = phys(i, j, k);
                p[1] = phys(i + 1, j, k);
                p[2] = phys(i + 1, j + 1, k);
                p[3] = phys(i, j + 1, k);
                p[4] = phys(i, j, k + 1);
                p[5] = phys(i + 1, j, k + 1);
                p[6] = phys(i + 1, j + 1, k + 1);
                p[7] = phys(i, j + 1, k + 1);

                function interp(pa: THREE.Vector3, pb: THREE.Vector3, va: number, vb: number): { x: number; y: number; z: number } {
                    const denom = (vb - va);
                    let t: number;
                    if (Math.abs(denom) < 1e-20) t = 0.5; else t = (iso - va) / denom;
                    if (t < 0) t = 0; else if (t > 1) t = 1;
                    return { x: lerp(pa.x, pb.x, t), y: lerp(pa.y, pb.y, t), z: lerp(pa.z, pb.z, t) };
                }

                // Marching tetrahedra
                for (let tIdx = 0; tIdx < tetrahedra.length; tIdx++) {
                    const tet = tetrahedra[tIdx];
                    if (!tet) continue;
                    const [a, b, c, d] = tet;
                    const va = v[a] ?? 0, vb = v[b] ?? 0, vc = v[c] ?? 0, vd = v[d] ?? 0;
                    const pa = p[a]!, pb = p[b]!, pc = p[c]!, pd = p[d]!;
                    const ma = va < iso, mb = vb < iso, mc = vc < iso, md = vd < iso;
                    const inside: Array<{ p: THREE.Vector3; v: number }> = [];
                    const outside: Array<{ p: THREE.Vector3; v: number }> = [];
                    if (ma) inside.push({ p: pa, v: va }); else outside.push({ p: pa, v: va });
                    if (mb) inside.push({ p: pb, v: vb }); else outside.push({ p: pb, v: vb });
                    if (mc) inside.push({ p: pc, v: vc }); else outside.push({ p: pc, v: vc });
                    if (md) inside.push({ p: pd, v: vd }); else outside.push({ p: pd, v: vd });
                    if (inside.length === 0 || inside.length === 4) continue;

                    function ip(p1: THREE.Vector3, v1: number, p2: THREE.Vector3, v2: number): { x: number; y: number; z: number } { return interp(p1, p2, v1, v2); }

                    if (inside.length === 1) {
                        const i0 = inside[0]!; const o0 = outside[0]!, o1 = outside[1]!, o2 = outside[2]!;
                        const tv0 = ip(i0.p, i0.v, o0.p, o0.v);
                        const tv1 = ip(i0.p, i0.v, o1.p, o1.v);
                        const tv2 = ip(i0.p, i0.v, o2.p, o2.v);
                        positions.push(tv0.x, tv0.y, tv0.z, tv1.x, tv1.y, tv1.z, tv2.x, tv2.y, tv2.z);
                        const abx = tv1.x - tv0.x, aby = tv1.y - tv0.y, abz = tv1.z - tv0.z;
                        const acx = tv2.x - tv0.x, acy = tv2.y - tv0.y, acz = tv2.z - tv0.z;
                        let nnx = aby * acz - abz * acy; let nny = abz * acx - abx * acz; let nnz = abx * acy - aby * acx;
                        const len = Math.hypot(nnx, nny, nnz) || 1.0; nnx /= len; nny /= len; nnz /= len;
                        normals.push(nnx, nny, nnz, nnx, nny, nnz, nnx, nny, nnz);
                    } else if (inside.length === 3) {
                        const o = outside[0]!; const i0 = inside[0]!, i1 = inside[1]!, i2 = inside[2]!;
                        const tv0 = ip(o.p, o.v, i0.p, i0.v);
                        const tv1 = ip(o.p, o.v, i1.p, i1.v);
                        const tv2 = ip(o.p, o.v, i2.p, i2.v);
                        positions.push(tv0.x, tv0.y, tv0.z, tv1.x, tv1.y, tv1.z, tv2.x, tv2.y, tv2.z);
                        const abx = tv1.x - tv0.x, aby = tv1.y - tv0.y, abz = tv1.z - tv0.z;
                        const acx = tv2.x - tv0.x, acy = tv2.y - tv0.y, acz = tv2.z - tv0.z;
                        let nnx = aby * acz - abz * acy; let nny = abz * acx - abx * acz; let nnz = abx * acy - aby * acx;
                        const len = Math.hypot(nnx, nny, nnz) || 1.0; nnx /= len; nny /= len; nnz /= len;
                        normals.push(nnx, nny, nnz, nnx, nny, nnz, nnx, nny, nnz);
                    } else if (inside.length === 2) {
                        const i0 = inside[0]!, i1 = inside[1]!; const o0 = outside[0]!, o1 = outside[1]!;
                        const a0 = ip(i0.p, i0.v, o0.p, o0.v);
                        const a1 = ip(i0.p, i0.v, o1.p, o1.v);
                        const b0 = ip(i1.p, i1.v, o0.p, o0.v);
                        const b1 = ip(i1.p, i1.v, o1.p, o1.v);
                        // two triangles (a0,a1,b1) and (a0,b1,b0)
                        positions.push(a0.x, a0.y, a0.z, a1.x, a1.y, a1.z, b1.x, b1.y, b1.z);
                        let abx = a1.x - a0.x, aby = a1.y - a0.y, abz = a1.z - a0.z;
                        let acx = b1.x - a0.x, acy = b1.y - a0.y, acz = b1.z - a0.z;
                        let nnx = aby * acz - abz * acy; let nny = abz * acx - abx * acz; let nnz = abx * acy - aby * acx; let len = Math.hypot(nnx, nny, nnz) || 1.0; nnx /= len; nny /= len; nnz /= len;
                        normals.push(nnx, nny, nnz, nnx, nny, nnz, nnx, nny, nnz);

                        positions.push(a0.x, a0.y, a0.z, b1.x, b1.y, b1.z, b0.x, b0.y, b0.z);
                        abx = b1.x - a0.x; aby = b1.y - a0.y; abz = b1.z - a0.z;
                        acx = b0.x - a0.x; acy = b0.y - a0.y; acz = b0.z - a0.z;
                        nnx = aby * acz - abz * acy; nny = abz * acx - abx * acz; nnz = abx * acy - aby * acx; len = Math.hypot(nnx, nny, nnz) || 1.0; nnx /= len; nny /= len; nnz /= len;
                        normals.push(nnx, nny, nnz, nnx, nny, nnz, nnx, nny, nnz);
                    }
                }
            }
        }
    }

    if (positions.length === 0) {
        console.warn('Contour: no triangles generated for isoValue', isoValue);
        return { contourMesh: null, contourMaterial: null };
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.computeBoundingSphere();

    const colorHex = (typeof materialColor === 'number') ? materialColor : 0xff66aa;
    const opacity = (typeof materialOpacity === 'number') ? materialOpacity : 0.6;
    const material = new THREE.MeshStandardMaterial({ color: colorHex, transparent: opacity < 1.0, opacity, depthWrite: true, side: THREE.DoubleSide, metalness: 0.15, roughness: 0.6 });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true; mesh.receiveShadow = true;
    mesh.name = 'ContourIsosurface';
    scene.add(mesh);
    return { contourMesh: mesh, contourMaterial: material };
}

interface DestroyContourCtx {
    scene: THREE.Scene;
    contourMesh: THREE.Mesh | null;
}

export function destroyContour(ctx: DestroyContourCtx): void {
    const { contourMesh } = ctx;
    if (contourMesh) {
        try { if (contourMesh.parent) contourMesh.parent.remove(contourMesh); } catch { /* swallow */ }
        try {
            if (contourMesh.geometry && contourMesh.geometry.dispose) contourMesh.geometry.dispose();
            if (contourMesh.material) {
                if (Array.isArray(contourMesh.material)) contourMesh.material.forEach((m) => { if (m && m.dispose) m.dispose(); });
                else if (contourMesh.material.dispose) contourMesh.material.dispose();
            }
        } catch { /* swallow */ }
    }
}

export function updateContour(ctx: CreateContourCtx & DestroyContourCtx): ContourResult {
    destroyContour(ctx);
    return createContour(ctx);
}

// === High-level orchestration over viewer.objects.contours.meshes ===
//
// Two flavours of mesh share viewer.objects.contours.meshes, distinguished by
// userData.isPreview:
//   - committed: persistent contours added via "Add" — survive tab switches.
//   - preview:   a single live, ghosted (more transparent) surface that tracks
//                the Value/Domain while the Contours tab is open and is dropped
//                on tab-leave unless committed.
// viewer.objects.contours.preview is an O(1) ref to that single preview mesh.

// Monotonic token guarding async preview builds: a newer previewNow(), a
// clearPreview, or a clearAll bumps it so a stale in-flight build discards its
// result instead of leaving an orphaned ghost in the scene.
let previewToken = 0;

// Preview meshes render at this fraction of the committed opacity so the
// uncommitted live surface reads as a ghost, distinct from the stack.
const PREVIEW_OPACITY_FACTOR = 0.45;

function disposeMesh(mesh: THREE.Mesh | null): void {
    if (!mesh) return;
    try { mesh.geometry?.dispose(); } catch { /* swallow */ }
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => { try { m?.dispose(); } catch { /* swallow */ } });
}

function setMeshOpacity(mesh: THREE.Mesh, alpha: number): void {
    const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    if (!mat) return;
    mat.opacity = alpha;
    mat.transparent = alpha < 1.0;
    mat.needsUpdate = true;
}

/** Remove the tracked preview mesh from scene + meshes list (no token bump). */
function removePreviewMesh(viewer: ViewerContext): void {
    const prev = viewer.objects.contours.preview;
    if (!prev) return;
    const scene = viewer.three.scene;
    if (scene) destroyContour({ scene, contourMesh: prev }); else disposeMesh(prev);
    const idx = viewer.objects.contours.meshes.indexOf(prev);
    if (idx >= 0) viewer.objects.contours.meshes.splice(idx, 1);
    viewer.objects.contours.preview = null;
}

/**
 * Build a contour isosurface mesh at the current contour params via the worker.
 * Pure async geometry/material build — does NOT add to the scene or track it.
 * Returns null for an empty/degenerate result. Caller decides preview vs commit.
 */
async function buildContourMeshAsync(viewer: ViewerContext, params: ParamsState): Promise<THREE.Mesh | null> {
    const component = viewer.field.currentComponent;
    // Domain → AABB clip. Box → exact AABB; sphere → its AABB (worker filters to
    // that) plus the exact main-thread sphere mask below; 'full' → no clip.
    const domain = resolveDomainBounds(params.selections, params.contourDomain);
    const g = viewer.grid;
    let clipBox = {
        enabled: false,
        xmin: g.xMin, xmax: g.xMax,
        ymin: g.yMin, ymax: g.yMax,
        zmin: g.zMin, zmax: g.zMax,
    };
    if (domain.kind === 'box') {
        clipBox = {
            enabled: true,
            xmin: domain.lower[0], xmax: domain.upper[0],
            ymin: domain.lower[1], ymax: domain.upper[1],
            zmin: domain.lower[2], zmax: domain.upper[2],
        };
    } else if (domain.kind === 'sphere') {
        const r = domain.radius;
        clipBox = {
            enabled: true,
            xmin: Math.max(g.xMin, domain.center[0] - r), xmax: Math.min(g.xMax, domain.center[0] + r),
            ymin: Math.max(g.yMin, domain.center[1] - r), ymax: Math.min(g.yMax, domain.center[1] + r),
            zmin: Math.max(g.zMin, domain.center[2] - r), zmax: Math.min(g.zMax, domain.center[2] + r),
        };
    }
    const positions = await generateContourAsync(component, params.contourValue, clipBox);
    if (!positions || positions.length === 0) return null;

    // Second-stage safety clip on main thread (worker may overshoot near
    // boundaries, and a sphere domain still needs the exact sphere mask here).
    const inside = (x: number, y: number, z: number): boolean => {
        if (clipBox.enabled && (
            x < clipBox.xmin || x > clipBox.xmax ||
            y < clipBox.ymin || y > clipBox.ymax ||
            z < clipBox.zmin || z > clipBox.zmax
        )) return false;
        if (domain.kind === 'sphere' && isOutsideDomain(x, y, z, domain)) return false;
        return true;
    };
    const filtered: number[] = [];
    for (let i = 0; i < positions.length; i += 9) {
        const x0 = positions[i] ?? 0, y0 = positions[i + 1] ?? 0, z0 = positions[i + 2] ?? 0;
        const x1 = positions[i + 3] ?? 0, y1 = positions[i + 4] ?? 0, z1 = positions[i + 5] ?? 0;
        const x2 = positions[i + 6] ?? 0, y2 = positions[i + 7] ?? 0, z2 = positions[i + 8] ?? 0;
        if (inside(x0, y0, z0) && inside(x1, y1, z1) && inside(x2, y2, z2)) {
            filtered.push(x0, y0, z0, x1, y1, z1, x2, y2, z2);
        }
    }
    if (filtered.length === 0) return null;
    const mapped = new Float32Array(filtered.length);
    for (let i = 0; i < filtered.length; i += 3) {
        const v = mapPhysicalToThreeWithRotation(new THREE.Vector3(filtered[i], filtered[i + 1], filtered[i + 2]));
        mapped[i] = v.x; mapped[i + 1] = v.y; mapped[i + 2] = v.z;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(mapped, 3));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    const c = strengthToColor(viewer, params, params.contourValue);
    const hex = (c && typeof c.r === 'number') ? ((c.r << 16) | (c.g << 8) | c.b) : 0xff66aa;
    const opacity = (typeof params.contourOpacity === 'number') ? params.contourOpacity : 0.6;
    const metalness = (typeof params.contourMetalness === 'number') ? params.contourMetalness : 0.05;
    const roughness = (typeof params.contourRoughness === 'number') ? params.contourRoughness : 0.9;
    const envIntensity = (typeof params.contourEnvIntensity === 'number') ? params.contourEnvIntensity : 0.0;
    const doubleSide = (typeof params.contourDoubleSide === 'boolean') ? params.contourDoubleSide : true;
    const depthWrite = (typeof params.contourDepthWrite === 'boolean') ? params.contourDepthWrite : true;
    const material = new THREE.MeshStandardMaterial({
        color: hex,
        transparent: opacity < 1.0,
        opacity, depthWrite,
        side: doubleSide ? THREE.DoubleSide : THREE.FrontSide,
        metalness, roughness, envMapIntensity: envIntensity,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true; mesh.receiveShadow = true;
    mesh.name = 'ContourIsosurface';
    if (typeof params.contourRenderOrder === 'number') mesh.renderOrder = params.contourRenderOrder;
    return mesh;
}

/** Committed generation: append a persistent contour mesh at the current
 *  params. Shows the task spinner. Used by the Add fallback (no preview yet). */
export function generateNow(viewer: ViewerContext, params: ParamsState): void {
    const scene = viewer.three.scene;
    if (!scene) return;
    store.dispatch(INTENTS.UI_TASK_SHOW, { msg: 'Generating contour…' });
    const run = async (): Promise<void> => {
        try {
            const mesh = await buildContourMeshAsync(viewer, params);
            store.dispatch(INTENTS.UI_TASK_HIDE);
            if (!mesh) return;
            mesh.userData['isPreview'] = false;
            mesh.visible = params.showContours !== false;
            scene.add(mesh);
            viewer.objects.contours.meshes.push(mesh);
        } catch (e) {
            store.dispatch(INTENTS.UI_TASK_HIDE);
            debugWarn(viewer, 'Contour generation failed:', e);
        }
    };
    requestAnimationFrame(() => { void run(); });
}

/** Regenerate the single live (ghosted) preview mesh at the current params.
 *  Token-guarded so rapid slider drags / tab-leave discard stale results. No
 *  task spinner — this fires on every Value/Domain change. */
export function previewNow(viewer: ViewerContext, params: ParamsState): void {
    const scene = viewer.three.scene;
    if (!scene) return;
    const tok = ++previewToken;
    const run = async (): Promise<void> => {
        try {
            const mesh = await buildContourMeshAsync(viewer, params);
            if (tok !== previewToken) { disposeMesh(mesh); return; } // superseded / cleared
            if (!mesh) { removePreviewMesh(viewer); return; }        // nothing at this value
            removePreviewMesh(viewer);                                // swap once new geometry is ready
            mesh.userData['isPreview'] = true;
            const base = (typeof params.contourOpacity === 'number') ? params.contourOpacity : 0.6;
            setMeshOpacity(mesh, base * PREVIEW_OPACITY_FACTOR);
            scene.add(mesh);
            viewer.objects.contours.meshes.push(mesh);
            viewer.objects.contours.preview = mesh;
        } catch (e) {
            if (tok === previewToken) debugWarn(viewer, 'Contour preview failed:', e);
        }
    };
    requestAnimationFrame(() => { void run(); });
}

/** Drop the live preview (on tab-leave). Committed contours are untouched. */
export function clearPreview(viewer: ViewerContext): void {
    previewToken++; // invalidate any in-flight preview build
    removePreviewMesh(viewer);
}

/** Promote the current preview into a committed contour, then spawn a fresh
 *  ghost at the current value. Falls back to a committed generation when no
 *  preview mesh exists yet (e.g. mid first-compute). */
export function commitPreview(viewer: ViewerContext, params: ParamsState): void {
    const prev = viewer.objects.contours.preview;
    if (prev) {
        prev.userData['isPreview'] = false;
        prev.visible = params.showContours !== false;
        setMeshOpacity(prev, (typeof params.contourOpacity === 'number') ? params.contourOpacity : 0.6);
        viewer.objects.contours.preview = null;
        previewToken++; // detach any in-flight preview from the just-promoted mesh
        previewNow(viewer, params);
    } else {
        generateNow(viewer, params);
        previewNow(viewer, params);
    }
}

/** Destroy every tracked contour mesh (committed + preview), then sweep stray
 *  ContourIsosurface meshes by name. */
export function clearAll(viewer: ViewerContext): void {
    const scene = viewer.three.scene;
    if (!scene) return;
    previewToken++; // invalidate any in-flight preview build
    viewer.objects.contours.preview = null;
    viewer.objects.contours.meshes.forEach((m) => destroyContour({ scene, contourMesh: m }));
    viewer.objects.contours.meshes.length = 0;
    const stray: THREE.Mesh[] = [];
    scene.traverse((obj) => { if (obj && (obj as THREE.Mesh).isMesh === true && obj.name === 'ContourIsosurface') stray.push(obj as THREE.Mesh); });
    stray.forEach((m) => destroyContour({ scene, contourMesh: m }));
}

export function applyOpacity(viewer: ViewerContext, val: number): void {
    const alpha = Math.max(0.0, Math.min(1.0, val));
    viewer.objects.contours.meshes.forEach((m) => {
        if (m && m.material) {
            // Keep the live preview proportionally ghosted as the user drags Opacity.
            setMeshOpacity(m, m.userData['isPreview'] ? alpha * PREVIEW_OPACITY_FACTOR : alpha);
        }
    });
}

export function applyRenderOrder(viewer: ViewerContext, val: number): void {
    const order = (typeof val === 'number') ? val : 0;
    viewer.objects.contours.meshes.forEach((m) => { if (m) m.renderOrder = order; });
}

/** Toggle visibility of the committed contour stack. The live preview ghost is
 *  left alone — it's transient on-tab authoring feedback, not part of the
 *  committed stack the user is hiding. */
export function applyVisibility(viewer: ViewerContext, visible: boolean): void {
    viewer.objects.contours.meshes.forEach((m) => {
        if (m && m.userData['isPreview'] !== true) m.visible = visible;
    });
}
